import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useUser } from '../../contexts/UserContext';
import LoadingSpinner from '../UI/LoadingSpinner';
import { Portfolio, StakingPosition } from '../../types';
import { stakingApi } from '../../services/api';

interface ChartData {
  name: string;
  value: number;
  color: string;
}

const PortfolioPage: React.FC = () => {
  const { user, isAuthenticated } = useUser();
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [stakingPositions, setStakingPositions] = useState<StakingPosition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'positions' | 'staking'>('overview');

  useEffect(() => {
    if (isAuthenticated) {
      loadPortfolioData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadPortfolioData = async () => {
    try {
      setIsLoading(true);
      
      const response = await stakingApi.getPortfolio();
      
      if (response.success && response.data) {
        setPortfolio(response.data);
        
        // Mock staking positions for now
        const mockStakingPositions: StakingPosition[] = [
          {
            id: '1',
            user_id: user?.id || '',
            property_id: '1',
            shares_staked: 50,
            staking_start_date: '2024-01-01',
            compound_interest_rate: 0.08,
            total_dividends_earned: 1200,
            status: 'active',
            created_at: '2024-01-01',
            updated_at: '2024-01-01',
            current_value: 5000,
            yield_to_date: 0.12,
            days_staked: 180
          },
          {
            id: '2',
            user_id: user?.id || '',
            property_id: '2',
            shares_staked: 30,
            staking_start_date: '2024-02-01',
            compound_interest_rate: 0.06,
            total_dividends_earned: 800,
            status: 'active',
            created_at: '2024-02-01',
            updated_at: '2024-02-01',
            current_value: 3000,
            yield_to_date: 0.10,
            days_staked: 150
          }
        ];
        setStakingPositions(mockStakingPositions);
      } else {
        toast.error('Failed to load portfolio data');
      }
    } catch (error) {
      console.error('Failed to load portfolio data:', error);
      toast.error('Failed to load portfolio data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
  };

  const getPieChartData = (): ChartData[] => {
    if (!portfolio?.positions) return [];
    
    return portfolio.positions.map((position, index) => ({
      name: position.property_name,
      value: position.current_value,
      color: [
        '#f59e0b', '#10b981', '#3b82f6', '#ef4444', 
        '#8b5cf6', '#f97316', '#06b6d4', '#84cc16'
      ][index % 8]
    }));
  };

  const getPerformanceData = () => {
    // Mock performance data
    return [
      { month: 'Jan', value: 45000, invested: 40000 },
      { month: 'Feb', value: 48000, invested: 42000 },
      { month: 'Mar', value: 52000, invested: 45000 },
      { month: 'Apr', value: 49000, invested: 47000 },
      { month: 'May', value: 55000, invested: 50000 },
      { month: 'Jun', value: 58000, invested: 52000 },
    ];
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-neutral-600 mb-4">Please log in to view your portfolio</p>
          <Link to="/login" className="btn-primary">
            Log In
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!portfolio) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">No Portfolio Yet</h2>
          <p className="text-neutral-600 mb-6">Start building your real estate portfolio today</p>
          <Link to="/properties" className="btn-primary">
            Browse Properties
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h1 className="text-4xl font-bold text-neutral-900 mb-4">
          Your Portfolio
        </h1>
        <p className="text-xl text-neutral-600">
          Track your real estate investments and performance
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <div className="card p-6 text-center">
          <h3 className="text-sm text-neutral-600 mb-2">Total Value</h3>
          <p className="text-3xl font-bold text-neutral-900">
            {formatCurrency(portfolio.total_value)}
          </p>
        </div>

        <div className="card p-6 text-center">
          <h3 className="text-sm text-neutral-600 mb-2">Total Invested</h3>
          <p className="text-3xl font-bold text-neutral-900">
            {formatCurrency(portfolio.total_invested)}
          </p>
        </div>

        <div className="card p-6 text-center">
          <h3 className="text-sm text-neutral-600 mb-2">Total Return</h3>
          <p className={`text-3xl font-bold ${
            portfolio.total_return >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {formatCurrency(portfolio.total_return)}
          </p>
          <p className={`text-sm ${
            portfolio.return_percentage >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {formatPercentage(portfolio.return_percentage)}
          </p>
        </div>

        <div className="card p-6 text-center">
          <h3 className="text-sm text-neutral-600 mb-2">Total Dividends</h3>
          <p className="text-3xl font-bold text-green-600">
            {formatCurrency(portfolio.total_dividends)}
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="border-b border-neutral-200"
      >
        <nav className="flex space-x-8">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'positions', label: 'Positions' },
            { key: 'staking', label: 'Staking' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setSelectedTab(key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                selectedTab === key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </motion.div>

      <motion.div
        key={selectedTab}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {selectedTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="card p-6">
              <h3 className="text-xl font-semibold text-neutral-900 mb-6">
                Portfolio Allocation
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={getPieChartData()}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {getPieChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card p-6">
              <h3 className="text-xl font-semibold text-neutral-900 mb-6">
                Performance Over Time
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getPerformanceData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      formatter={(value) => formatCurrency(Number(value))}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Bar dataKey="invested" fill="#94a3b8" name="Invested" />
                    <Bar dataKey="value" fill="#10b981" name="Current Value" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'positions' && (
          <div className="space-y-6">
            {portfolio.positions && portfolio.positions.length > 0 ? (
              portfolio.positions.map((position) => (
                <div key={position.property_id} className="card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-neutral-900">
                        {position.property_name}
                      </h3>
                      <p className="text-sm text-neutral-600">
                        {position.shares_owned.toLocaleString()} shares owned
                        {position.shares_staked > 0 && (
                          <span className="ml-2 text-blue-600">
                            ({position.shares_staked.toLocaleString()} staked)
                          </span>
                        )}
                      </p>
                    </div>
                    <Link
                      to={`/properties/${position.property_id}`}
                      className="btn-outline"
                    >
                      View Property
                    </Link>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-neutral-600">Investment</p>
                      <p className="text-lg font-semibold text-neutral-900">
                        {formatCurrency(position.investment_amount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Current Value</p>
                      <p className="text-lg font-semibold text-neutral-900">
                        {formatCurrency(position.current_value)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Return</p>
                      <p className={`text-lg font-semibold ${
                        position.return_amount >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(position.return_amount)}
                      </p>
                      <p className={`text-sm ${
                        position.return_percentage >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatPercentage(position.return_percentage)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Dividends Earned</p>
                      <p className="text-lg font-semibold text-green-600">
                        {formatCurrency(position.dividends_earned)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-neutral-600 mb-4">No positions yet</p>
                <Link to="/properties" className="btn-primary">
                  Start Investing
                </Link>
              </div>
            )}
          </div>
        )}

        {selectedTab === 'staking' && (
          <div className="space-y-6">
            {stakingPositions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {stakingPositions.map((position) => (
                  <div key={position.id} className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-neutral-900">
                        Property #{position.property_id}
                      </h3>
                      <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                        position.status === 'active' ? 'bg-green-100 text-green-700' :
                        position.status === 'unlocked' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {position.status}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-neutral-600">Shares Staked</span>
                        <span className="text-sm font-medium">{position.shares_staked.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-neutral-600">Current Value</span>
                        <span className="text-sm font-medium">{formatCurrency(position.current_value)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-neutral-600">Dividends Earned</span>
                        <span className="text-sm font-medium text-green-600">
                          {formatCurrency(position.total_dividends_earned)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-neutral-600">Yield to Date</span>
                        <span className="text-sm font-medium text-green-600">
                          {formatPercentage(position.yield_to_date * 100)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-neutral-600">Days Staked</span>
                        <span className="text-sm font-medium">{position.days_staked}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-neutral-600">Interest Rate</span>
                        <span className="text-sm font-medium">
                          {formatPercentage(position.compound_interest_rate * 100)}
                        </span>
                      </div>
                    </div>

                    {position.status === 'active' && (
                      <div className="mt-4 pt-4 border-t border-neutral-200">
                        <button className="btn-outline w-full">
                          Unstake Position
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-neutral-600 mb-4">No staking positions yet</p>
                <p className="text-sm text-neutral-500 mb-6">
                  Stake your shares to earn additional compound interest
                </p>
                <Link to="/properties" className="btn-primary">
                  Explore Properties
                </Link>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PortfolioPage;
